import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { HashRouter, BrowserRouter, Prompt } from 'react-router-dom';
import classnames from 'classnames';

import DataLoadError from 'components/DataLoadError';
import {
  GLOBAL_POPOVER_CONTAINER_ID,
  POPOVER_CONTAINER_ID,
  POPOVER_LVL2_CONTAINER_ID,
  SCROLL_CONTAINER_ID,
} from 'utils/appLayout';
import { isAppLoadedOnLegacyUrl } from 'utils/navigation';

import SidePanel from 'pages/ReactRoot/containers/SidePanel';
import ReactRoot from 'pages/ReactRoot';
import { useConstantRefCallback } from '@xmatters/react';
import AppScaleStyleSheet from 'Application/AppScaleStyleSheet';

import HelpView from '../HelpView';

import styles from './ApplicationShell.module.scss';
import './default-app-theme.scss';

const beforeUnloadListener = e => {
  // this string doesn't matter - browsers do not respect it
  // but it's needed to show confirm dialog
  e.returnValue = 'are you sure?';
  return e;
};

export function getRightContentContainer() {
  return document.querySelector(`.${styles.rightContent}`);
}

export default function ApplicationShell({
  xmApiCallError,
  chickenExitMessageId,
  hasSidePanel,
  sidePanelWidth,
  preserveMinWidth,
  header,
  footer,
  content,
  nav,
  isEmbedded,
  routerBasename,
  isDirty,
  setPageDirty,
}) {
  useEffect(() => {
    if (isDirty) {
      window.addEventListener('beforeunload', beforeUnloadListener);
    } else {
      window.removeEventListener('beforeunload', beforeUnloadListener);
    }

    return () =>
      window.removeEventListener('beforeunload', beforeUnloadListener);
  }, [isDirty]);

  const getUserConfirmation = useConstantRefCallback((message, callback) => {
    const haveUnsavedChanges = isDirty || !!window.XM_GWT_PAGE_DIRTY?.();
    /* eslint-disable-next-line no-alert */
    const allowTransition = !haveUnsavedChanges || window.confirm(message);
    callback(allowTransition);
    if (allowTransition) {
      window.XM_hideGWTValidations?.();
    }
    setPageDirty({ isDirty: !allowTransition });
    if (haveUnsavedChanges && allowTransition) {
      window.XM_GWT_RESET_PAGE_DIRTY?.();
    }
  });

  if (xmApiCallError) {
    return (
      <DataLoadError
        className={styles.error}
        message={<FormattedMessage id="DATA_LOAD_ERROR" />}
      />
    );
  }

  // have to use HashRouter for legacy urls for now as GWT doesn't handle pushState
  const Router = isAppLoadedOnLegacyUrl() ? HashRouter : BrowserRouter;

  return (
    <Router
      hashType={isAppLoadedOnLegacyUrl() ? 'noslash' : undefined}
      getUserConfirmation={getUserConfirmation}
      basename={routerBasename}
    >
      <AppScaleStyleSheet />
      <div className={styles.pageWrap}>
        {isDirty && (
          <FormattedMessage id={chickenExitMessageId}>
            {msg => <Prompt message={msg[0]} />}
          </FormattedMessage>
        )}
        {header}
        <main className={styles.pageBody}>
          {nav}
          <div className={styles.rightContent}>
            <div
              id={SCROLL_CONTAINER_ID}
              className={classnames(styles.scrollContainer, {
                [styles.withSidePanel]: hasSidePanel && !preserveMinWidth,
                [styles.sidePanelTransition]: hasSidePanel,
              })}
              style={hasSidePanel ? { right: sidePanelWidth } : null}
            >
              <div id="wrap">
                <div id="content-wrapper">{content}</div>
              </div>
              <ReactRoot isEmbedded={isEmbedded} />
              {footer}
              <div
                id={POPOVER_CONTAINER_ID}
                className={styles.popoverContainer}
              />
              <div
                id={POPOVER_LVL2_CONTAINER_ID}
                className={styles.popoverContainerLvl2}
              />
            </div>
            <SidePanel />
          </div>
          <div
            id={GLOBAL_POPOVER_CONTAINER_ID}
            className={styles.popoverContainer}
          />
        </main>
      </div>
      <HelpView />
    </Router>
  );
}

ApplicationShell.displayName = 'ApplicationShell';
ApplicationShell.propTypes = {
  isDirty: PropTypes.bool.isRequired,
  chickenExitMessageId: PropTypes.string,
  header: PropTypes.node,
  content: PropTypes.node.isRequired,
  footer: PropTypes.node,
  nav: PropTypes.node,
  isEmbedded: PropTypes.bool,
  routerBasename: PropTypes.string,
  xmApiCallError: PropTypes.bool.isRequired,
  setPageDirty: PropTypes.func.isRequired,
  hasSidePanel: PropTypes.bool.isRequired,
  sidePanelWidth: PropTypes.number,
  preserveMinWidth: PropTypes.bool,
};
ApplicationShell.defaultProps = {
  chickenExitMessageId: 'YOU_HAVE_UNSAVED_CHANGES',
  header: null,
  footer: null,
  nav: null,
  isEmbedded: false,
  routerBasename: undefined,
  sidePanelWidth: null,
  preserveMinWidth: false,
};
